import { SIGNUP_JOURNEY_ID, signupJourney, type SignupJourneyMark } from '@common/metrics/journey/journeys/signup';
import { Public_Routes } from '@common/url';
import { getProductTypeFromBasePath } from '@common/urlBasePath';
import { signup } from '@common/user-analytics';
import { UserTypes } from '@common/UserTypes';
import { useOpenAIPixel } from '@pages/auth/analyticsHooks';
import { NoMatch } from 'frontend/core/src/router/common';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUnsecuredGlobalVariables } from '../../../context/globalConfig/unsecuredConfigHook';
import { useMixPanel } from '../../../context/mixpanel/context';
import { useLinkedInTrack, useSignupParamsFromURL, useZoomInfo } from '../common';
import { OtpForm } from './OtpForm';
import { PasswordForm } from './Password';
import { SignUpForm } from './SignUpForm';

enum StepType {
  otp = 'otp',
  password = 'password',
  signup = 'signup'
}

const STEP_MARK: Record<StepType, SignupJourneyMark> = {
  [StepType.otp]: 'stepOtpAt',
  [StepType.password]: 'stepPasswordAt',
  [StepType.signup]: 'stepSignupAt'
};

export default function Page() {
  const navigate = useNavigate();
  const unsecureGlobalConfigManager = useUnsecuredGlobalVariables();
  const { firstName, lastName, email, otp, verifiedUser, invitation, teamid, stepParam, uid, company, redirect } =
    useSignupParamsFromURL();
  const step: StepType = stepParam
    ? (stepParam as StepType)
    : otp || (invitation && firstName && email)
      ? StepType.otp
      : StepType.signup;
  const isSignupEnabled = unsecureGlobalConfigManager.isSignupEnabled;
  const { track, identifyAndSetPeople } = useMixPanel();

  const productType = getProductTypeFromBasePath();

  useLinkedInTrack();
  useOpenAIPixel();

  useZoomInfo();

  useEffect(() => {
    signupJourney.begin(SIGNUP_JOURNEY_ID);
    signupJourney.mark(SIGNUP_JOURNEY_ID, 'mountedAt');
  }, []);

  useEffect(() => {
    if (invitation === 'true' && firstName) {
      identifyAndSetPeople(email, {
        firstName,
        lastName,
        source: UserTypes.CeFree,
        invitation: 'true',
        userId: uid,
        company,
        signup_date: new Date().toISOString()
      });
      track(signup.normal[step], { email, lastName, firstName });
    } else if (firstName) {
      identifyAndSetPeople(email, {
        firstName,
        lastName,
        company,
        source: UserTypes.CeFree,
        userId: uid,
        signup_date: new Date().toISOString()
      });
    }
    if (step !== 'signup') {
      track(signup.normal[step], { email, firstName });
    }
    // Unconditional and separate from the MixPanel track() calls above (which skip the bare
    // 'signup' step): every step, including the initial one, is a real journey mark.
    signupJourney.mark(SIGNUP_JOURNEY_ID, STEP_MARK[step]);
  }, [step, company, identifyAndSetPeople, track, firstName, lastName, email, invitation, uid]);

  const gotoNextStep = (step: StepType, params: { email: string }) => {
    navigate(Public_Routes.SignUp.getUrlWithBasePath(undefined, { ...params, step, redirect }), { replace: true });
  };

  let form: React.ReactNode;
  switch (step) {
    case 'signup': {
      form = isSignupEnabled ? <SignUpForm productType={productType} /> : <NoMatch />;
      break;
    }
    case 'otp': {
      form = (
        <OtpForm
          email={email}
          otp={otp}
          teamid={teamid}
          gotoNextStep={(params) => gotoNextStep(StepType.password, params)}
          verifiedUser={verifiedUser}
          invitation={invitation}
        />
      );
      break;
    }
    case 'password': {
      form = (
        <PasswordForm
          email={email}
          teamid={teamid}
          firstName={firstName}
          lastName={lastName}
          company={company}
          preserveSession
        />
      );
      break;
    }
  }
  return <>{form}</>;
}
